import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/import-export`.
 *
 * Two surfaces:
 *   - export(): trigger a browser download of the ZIP envelope (scope =
 *     admin, optional entity allowlist). We ask axios for a Blob response
 *     so we can save-as directly instead of a `window.location = URL` hack
 *     that would bypass the bearer-token interceptor.
 *   - importZip(): POST multipart/form-data (so the file travels through
 *     the standard axios pipeline with the Authorization header). Server
 *     parses the multipart + extracts the .zip file; dry-run by default.
 */

/** Canonical entity slugs mirroring the backend registry. */
export type AdminEntitySlug =
  | 'task'
  | 'adventure'
  | 'cohort'
  | 'sweepstakes'
  | 'terms_of_service'
  | 'task_reward_collection'
  | 'plan_cohort_purse_mapping'
  | 'questionnaire'
  | 'task_type'
  | 'task_category'
  | 'task_reward_type'
  | 'wallet_type'
  | 'notification_channel'
  | 'notification_category'
  | 'component_type'
  | 'onboarding_component'
  | 'canonical_field'
  | 'cohort_attribute_def'
  | 'tenant_attr_definition';

export interface ImportEntityDiff {
  to_insert: number;
  to_update: number;
  to_skip: number;
  errors: string[];
}

export interface ImportDiffResponse {
  mode: 'dry-run' | 'apply';
  scope: 'admin';
  summary: Record<string, ImportEntityDiff>;
}

export interface ExportParams {
  entities?: AdminEntitySlug[] | 'all';
}

const BASE = '/api/admin/import-export';

/**
 * Triggers a browser download of the export ZIP. We use axios (blob
 * response) rather than `window.location.assign(...)` so the bearer-token
 * request interceptor still wraps the call. Then synthesize an anchor
 * click to save the blob with its original filename.
 */
export async function exportAdminZip(params: ExportParams = {}): Promise<void> {
  const qs = new URLSearchParams();
  qs.set('scope', 'admin');
  if (params.entities && params.entities !== 'all') {
    qs.set('entities', params.entities.join(','));
  } else {
    qs.set('entities', 'all');
  }
  const res = await axiosClient.get(`${BASE}/export?${qs.toString()}`, {
    responseType: 'blob',
  });

  // Pull filename from Content-Disposition, fall back to a timestamped default.
  const contentDisposition = String(res.headers['content-disposition'] ?? '');
  const match = /filename="?([^"]+)"?/.exec(contentDisposition);
  const filename = match?.[1] ?? `admin-export-${Date.now()}.zip`;

  const blob = new Blob([res.data as BlobPart], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Uploads a ZIP envelope for validation (dry-run) or application.
 *
 * Uses application/zip directly rather than multipart/form-data — the
 * server accepts both, and application/zip sidesteps the extra boundary
 * parsing / multer dep. The browser's File object streams cleanly when
 * passed as the axios body.
 */
export async function importAdminZip(
  file: File,
  mode: 'dry-run' | 'apply' = 'dry-run',
): Promise<ImportDiffResponse> {
  const res = await axiosClient.post<ImportDiffResponse>(
    `${BASE}/import?mode=${mode}`,
    file,
    {
      headers: { 'Content-Type': 'application/zip' },
      // The server may return 207 Multi-Status on partial apply success.
      // axios defaults to only accepting 2xx; extend to cover 207.
      validateStatus: (s) => (s >= 200 && s < 300) || s === 207,
    },
  );
  return res.data;
}
